import { PhoneLayoutProvider } from "./lib/PhoneLayout";
import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { MotionConfig, useReducedMotion } from "framer-motion";
import { Toaster } from "sonner";
import { StoreProvider } from "./store/StoreContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import QuickView from "./components/QuickView";
import CartDrawer from "./components/CartDrawer";
import WhatsAppButton from "./components/WhatsAppButton";
import BrandReveal from "./components/BrandReveal";
import ContentOverrides from "./components/ContentOverrides";
import { EditorialTextProvider } from "./lib/EditorialText";
import useVisualViewport from "./lib/useVisualViewport";
import Journal from "./pages/Journal";
import Home from "./pages/Home";
import BuyerAccess from "./pages/BuyerAccess";
import {BuyerProvider} from "./store/BuyerContext";
import Shop from "./pages/Shop";
import Collections from "./pages/Collections";
import CollectionPage from "./pages/CollectionPage";
import ProductPage from "./pages/ProductPage";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import About from "./pages/About";
import Contact from "./pages/Contact";
import TradeFairs from "./pages/TradeFairs";
import Compliance from "./pages/Compliance";
import Wishlist from "./pages/Wishlist";
import StaticPage from "./pages/StaticPage";
import NotFound from "./pages/NotFound";
const AdminApp = lazy(() => import("./admin/AdminApp"));

function ScrollAndLenis() {
  const { pathname, search, hash } = useLocation();
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const desktop = window.matchMedia("(min-width: 1024px) and (hover: hover) and (pointer: fine)");
    let lenis;
    let raf;
    const loop = (t) => {
      if (!lenis || document.hidden) return;
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    const destroy = () => {
      cancelAnimationFrame(raf);
      lenis?.destroy();
      lenis = null;
      delete window.__lenis;
    };
    const configure = () => {
      destroy();
      if (!desktop.matches) return;
      lenis = new Lenis({ duration: 1.1, smoothWheel: true });
      window.__lenis = lenis;
      if (document.documentElement.hasAttribute("data-dialog-open")) lenis.stop();
      if (!document.hidden) raf = requestAnimationFrame(loop);
    };
    const visibility = () => { cancelAnimationFrame(raf); if (!document.hidden && lenis) raf = requestAnimationFrame(loop); };
    configure();
    desktop.addEventListener("change", configure);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      desktop.removeEventListener("change", configure);
      document.removeEventListener("visibilitychange", visibility);
      destroy();
    };
  }, [reduced]);

  useEffect(() => {
    if (hash) {
      let id = hash.slice(1);
      try { id = decodeURIComponent(id); } catch { /* An invalid escape is treated as a literal fragment. */ }
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" }), 100);
        return;
      }
    }
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, search, hash]);

  return null;
}

function Storefront() {
  useVisualViewport();
  return (
    <StoreProvider><BuyerProvider><EditorialTextProvider>
      <ContentOverrides />
      <BrandReveal />
      <ScrollAndLenis />
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:z-[300] focus:top-2 focus:left-2 focus:bg-white focus:px-5 focus:py-3">Skip to content</a>
      <Header />
      <main id="main-content" className="storefront-main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/buyer-access" element={<BuyerAccess />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="/collections/:slug" element={<CollectionPage />} />
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/enquiry" element={<Checkout />} />
          <Route path="/catalogue" element={<Checkout />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/enquiry/received" element={<OrderConfirmation />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/trade-fairs" element={<TradeFairs />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/pages/:page" element={<StaticPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
      <QuickView />
      <CartDrawer />
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: { background: "#1B1B1B", color: "#FFFFFF", border: "none", borderRadius: 999, fontFamily: "Montserrat, sans-serif", fontSize: 13 },
        }}
      />
    </EditorialTextProvider></BuyerProvider></StoreProvider>
  );
}

function App() {
  return (
    <div className="App">
      <PhoneLayoutProvider><MotionConfig reducedMotion="user"><BrowserRouter basename={document.documentElement.dataset.showroomPreview ? "/admin/preview" : "/"}>
        <Routes>
          <Route path="/admin/*" element={<Suspense fallback={<p className="p-8" role="status">Loading administration…</p>}><AdminApp /></Suspense>} />
          <Route path="/*" element={<Storefront />} />
        </Routes>
      </BrowserRouter></MotionConfig></PhoneLayoutProvider>
    </div>
  );
}

export default App;
